import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { guardarDestino } from '@/lib/destino';
import { reduzirImagem } from '@/lib/imagem';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import AppScreen from '@/components/ui/AppScreen';
import { useAlturaElemento } from '@/hooks/useAlturaElemento';
import EstadoVazio from '@/components/ui/EstadoVazio';
import Modal from '@/components/ui/Modal';
import Sheet from '@/components/ui/Sheet';
import VisualizadorArte from '@/components/feature/VisualizadorArte';
import BolhaMensagem from '@/pages/gerenciar/assistente/components/BolhaMensagem';
import Composer from '@/pages/gerenciar/assistente/components/Composer';
import type { MensagemAssistente } from '@/pages/gerenciar/assistente/types';
import CartaoEventoCriacao from './components/CartaoEventoCriacao';
import { CHIPS_INICIAIS, ErroCriacao, chamarAssistenteCriar, diaMes, textoValorParticipacao, linhaData } from './utils';
import type {
  EnviarCriacaoResposta,
  EstadoCriacao,
  HistoricoCriacaoResposta,
  UsoCriacao,
} from './types';

const TIPOS_IMAGEM = ['image/jpeg', 'image/png', 'image/webp'];

export default function CriarConversaPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [prontoChat, setProntoChat] = useState(false);
  const [carregandoHistorico, setCarregandoHistorico] = useState(true);
  const [erroHistorico, setErroHistorico] = useState<string | null>(null);
  const [ligado, setLigado] = useState(true);

  const [criacaoId, setCriacaoId] = useState<number | null>(null);
  const [mensagens, setMensagens] = useState<MensagemAssistente[]>([]);
  const [estado, setEstado] = useState<EstadoCriacao | null>(null);
  const [uso, setUso] = useState<UsoCriacao | null>(null);

  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviandoImagem, setEnviandoImagem] = useState(false);
  const [limiteAtingido, setLimiteAtingido] = useState(false);

  const [sheetMenu, setSheetMenu] = useState(false);
  const [modalNovo, setModalNovo] = useState(false);
  const [sheetDetalhes, setSheetDetalhes] = useState(false);
  const [imagemAberta, setImagemAberta] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Altura real do composer fixo (muda quando o textarea cresce): serve para
  // reservar o espaço certo embaixo da conversa.
  const { ref: composerRef, altura: alturaComposer } = useAlturaElemento();

  // ——— Acesso: precisa estar logado (mesma regra da antiga /criar). ———
  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino('/criar');
      navigate('/entrar?voltar=/criar', { replace: true });
      return;
    }
    setProntoChat(true);
  }, [pronto, user, navigate]);

  const carregarHistorico = useCallback(async () => {
    setCarregandoHistorico(true);
    setErroHistorico(null);
    try {
      const resp = await chamarAssistenteCriar<HistoricoCriacaoResposta>({ acao: 'historico' });
      // Só retoma se a conversa estiver ABERTA. Se o evento dela já foi publicado
      // (ou a conversa foi encerrada), começa uma conversa nova.
      const situacao = resp.criacao?.situacao;
      const podeRetomar = !resp.criacao || !situacao || situacao === 'aberta';
      if (!podeRetomar) {
        try {
          await chamarAssistenteCriar({ acao: 'nova' });
        } catch {
          /* se não der, a tela segue vazia e a pessoa pode começar digitando */
        }
        setCriacaoId(null);
        setMensagens([]);
        setEstado(null);
        setUso(resp.uso ?? null);
        setLigado(resp.ligado !== false);
        return;
      }
      setCriacaoId(resp.criacao?.id ?? null);
      setMensagens(
        (resp.mensagens || []).map((m) => ({
          id: m.id,
          papel: m.papel,
          texto: m.texto,
          anexo_url: m.anexo_url ?? null,
          opcoes: m.opcoes,
          criado_em: m.criado_em,
        })),
      );
      setEstado(resp.estado ?? null);
      setUso(resp.uso ?? null);
      setLigado(resp.ligado !== false);
    } catch (e) {
      setErroHistorico(mensagemAmigavel(e, 'Não foi possível abrir o assistente agora.'));
    } finally {
      setCarregandoHistorico(false);
    }
  }, []);

  useEffect(() => {
    if (prontoChat) carregarHistorico();
  }, [prontoChat, carregarHistorico]);

  // Auto-rola para o fim ao abrir e depois de cada mudança na conversa
  // (mensagem nova, resposta pronta com os chips, erro). Rola até o fim do
  // documento, que já reserva a altura do composer + uma folga.
  useEffect(() => {
    if (carregandoHistorico) return;
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  }, [carregandoHistorico, mensagens]);

  // ——— Enviar uma mensagem (texto e/ou imagem já subida). ———
  const enviar = useCallback(
    async (valor: string, anexoUrl?: string | null, idPre?: string, interno = false) => {
      const conteudo = valor.trim();
      if ((!conteudo && !anexoUrl) || enviando || (enviandoImagem && !interno)) return;

      const userMsgId = idPre ?? `u-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const agora = new Date().toISOString();

      setTexto('');
      setEnviando(true);

      setMensagens((prev) => {
        if (idPre) {
          return prev.map((m) =>
            m.id === idPre
              ? { ...m, anexo_url: anexoUrl ?? m.anexo_url, enviandoAnexo: false, texto: conteudo, erro: undefined }
              : m,
          );
        }
        return [
          ...prev,
          {
            id: userMsgId,
            papel: 'usuario',
            texto: conteudo,
            anexo_url: anexoUrl ?? null,
            criado_em: agora,
          },
        ];
      });

      const idPensando = `p-${userMsgId}`;
      setMensagens((prev) => [
        ...prev,
        { id: idPensando, papel: 'assistente', texto: '', criado_em: agora, pendente: true },
      ]);

      try {
        const resp = await chamarAssistenteCriar<EnviarCriacaoResposta>({
          acao: 'enviar',
          criacao_id: criacaoId,
          mensagem: conteudo,
          anexo_url: anexoUrl ?? null,
        });
        setCriacaoId(resp.criacao?.id ?? criacaoId);
        setMensagens((prev) =>
          prev.map((m) =>
            m.id === idPensando
              ? {
                  id: `${idPensando}-r`,
                  papel: 'assistente',
                  texto: resp.resposta,
                  opcoes: resp.opcoes || [],
                  criado_em: new Date().toISOString(),
                }
              : m,
          ),
        );
        setEstado(resp.estado ?? null);
        setUso(resp.uso ?? null);
      } catch (e) {
        const erro = e instanceof ErroCriacao ? e : null;
        const mensagemErro = mensagemAmigavel(e, 'Não foi possível enviar. Tente de novo.');
        setMensagens((prev) =>
          prev
            .filter((m) => m.id !== idPensando)
            .map((m) => (m.id === userMsgId ? { ...m, erro: mensagemErro } : m)),
        );
        if (erro?.limite) setLimiteAtingido(true);
      } finally {
        setEnviando(false);
      }
    },
    [criacaoId, enviando, enviandoImagem],
  );

  function reenviar(m: MensagemAssistente) {
    setMensagens((prev) => prev.filter((x) => x.id !== m.id));
    enviar(m.texto, m.anexo_url ?? undefined);
  }

  // ——— Imagem: reduz no aparelho, sobe para "capas" e vira uma mensagem. ———
  async function aoEscolherArquivo(arquivo: File | null) {
    if (!arquivo || !user) return;
    if (!TIPOS_IMAGEM.includes(arquivo.type)) {
      mostrarToast('Use uma imagem jpg, png ou webp.', 3200, 'erro');
      return;
    }

    const textoAtual = texto;
    const idImg = `img-${Date.now()}`;
    const localUrl = URL.createObjectURL(arquivo);

    setTexto('');
    setEnviandoImagem(true);
    setMensagens((prev) => [
      ...prev,
      {
        id: idImg,
        papel: 'usuario',
        texto: textoAtual,
        anexo_url: localUrl,
        enviandoAnexo: true,
        criado_em: new Date().toISOString(),
      },
    ]);

    try {
      const blob = await reduzirImagem(arquivo, 1600, 0.85, 'image/jpeg');
      const caminho = `${user.id}/criar-${Date.now()}.jpg`;
      const { error } = await supabase.storage
        .from('capas')
        .upload(caminho, blob, { contentType: 'image/jpeg', upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
      const url = data.publicUrl;

      setEnviandoImagem(false);
      await enviar(textoAtual, url, idImg, true);
    } catch {
      setMensagens((prev) => prev.filter((m) => m.id !== idImg));
      setEnviandoImagem(false);
      if (textoAtual) setTexto(textoAtual);
      mostrarToast('Não consegui enviar a imagem. Tente de novo.', 3600, 'erro');
    } finally {
      URL.revokeObjectURL(localUrl);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  // ——— Começar outro evento. ———
  async function comecarOutro() {
    setModalNovo(false);
    setSheetMenu(false);
    try {
      await chamarAssistenteCriar({ acao: 'nova' });
      setCriacaoId(null);
      setMensagens([]);
      setEstado(null);
      setUso(null);
      setErroHistorico(null);
      setLimiteAtingido(false);
      setTexto('');
    } catch (e) {
      mostrarToast(mensagemAmigavel(e, 'Não foi possível agora.'), 3200, 'erro');
    }
  }

  const ocupado = enviando || enviandoImagem;

  if (!pronto || !prontoChat) {
    return (
      <AppScreen>
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="h-12 animate-pulse rounded-[18px] bg-background-200/70" />
          <div className="mt-4 h-20 animate-pulse rounded-[18px] bg-background-200/70" />
          <div className="mt-4 h-56 animate-pulse rounded-[18px] bg-background-200/70" />
        </div>
      </AppScreen>
    );
  }

  const conversaVazia = !carregandoHistorico && !erroHistorico && mensagens.length === 0;
  const ultima = mensagens[mensagens.length - 1];
  const opcoesAtivas =
    !ocupado && ultima && ultima.papel === 'assistente' && !ultima.pendente && ultima.opcoes?.length
      ? ultima.opcoes
      : undefined;

  return (
    <AppScreen>
      {/* Cabeçalho */}
      <header className="sticky top-0 z-40 border-b border-background-200/70 bg-background-100/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 w-full max-w-[720px] items-center gap-2 px-4">
          <button
            type="button"
            onClick={() => navigate('/meus-eventos')}
            aria-label="Voltar"
            className="aperta flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-800 transition-colors hover:bg-background-200/60"
          >
            <i className="ri-arrow-left-line text-xl" aria-hidden />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[18px] font-extrabold text-foreground-950">
            Novo evento
          </h1>
          <button
            type="button"
            onClick={() => setSheetMenu(true)}
            aria-label="Mais opções"
            className="aperta flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-800 transition-colors hover:bg-background-200/60"
          >
            <i className="ri-more-fill text-xl" aria-hidden />
          </button>
        </div>
      </header>

      {/* Cartão do evento */}
      {estado && (
        <div className="sticky top-14 z-30 border-b border-background-200/60 bg-background-100/95 px-4 py-2.5 backdrop-blur-md">
          <div className="mx-auto w-full max-w-[720px]">
            <CartaoEventoCriacao estado={estado} aoAbrirDetalhes={() => setSheetDetalhes(true)} />
          </div>
        </div>
      )}

      {/* Conversa */}
      <main
        className="mx-auto w-full max-w-[720px] px-4 pt-3"
        style={{ paddingBottom: alturaComposer + 16 }}
      >
        {carregandoHistorico && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`h-16 animate-pulse rounded-[18px] bg-background-200/60 ${
                  i % 2 === 0 ? 'ml-auto w-2/3' : 'w-3/4'
                }`}
              />
            ))}
          </div>
        )}

        {!carregandoHistorico && erroHistorico && (
          <EstadoVazio
            icone="ri-sparkling-2-line"
            titulo="Não foi possível abrir o assistente agora."
            descricao={erroHistorico}
            acaoRotulo="Tentar de novo"
            aoClicarAcao={carregarHistorico}
          />
        )}

        {!carregandoHistorico && !erroHistorico && !ligado && (
          <EstadoVazio
            icone="ri-sparkling-2-line"
            titulo="O assistente de criação ainda não está ligado."
            descricao="Enquanto isso, você pode criar o evento pelo formulário."
            acaoRotulo="Preencher o formulário"
            aoClicarAcao={() => navigate('/criar/formulario')}
          />
        )}

        {!carregandoHistorico && !erroHistorico && ligado && (
          <>
            {conversaVazia && (
              <div className="mb-4 rounded-[18px] bg-primary-50 p-4">
                <p className="text-[14px] leading-relaxed text-primary-900">
                  Vamos criar seu evento conversando. Conte que evento é, ou mande a arte (o
                  cartaz) que eu leio data e local.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {CHIPS_INICIAIS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => enviar(c)}
                      className="aperta flex min-h-[44px] cursor-pointer items-center whitespace-nowrap rounded-full bg-background-50 px-4 text-[13px] font-semibold text-primary-700 shadow-pcard transition-colors hover:bg-primary-100"
                    >
                      {c}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="aperta flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 text-[13px] font-semibold text-white transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-image-add-line" aria-hidden />
                    Mandar a arte
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/criar/formulario')}
                  className="aperta mt-2 inline-flex min-h-[44px] cursor-pointer items-center gap-1 whitespace-nowrap text-[13px] font-semibold text-primary-700 underline"
                >
                  Prefiro preencher o formulário
                </button>
              </div>
            )}

            <div className="space-y-3">
              {mensagens.map((m) => (
                <BolhaMensagem
                  key={m.id}
                  mensagem={m}
                  mostrarAcoes={false}
                  aoAbrirImagem={(url) => setImagemAberta(url)}
                  aoTentarDeNovo={m.erro && m.papel === 'usuario' ? () => reenviar(m) : undefined}
                  opcoes={opcoesAtivas && m.id === ultima.id ? opcoesAtivas : undefined}
                  aoEscolherOpcao={(t) => enviar(t)}
                />
              ))}
            </div>
          </>
        )}

      </main>

      {/* Composer */}
      {!carregandoHistorico && !erroHistorico && ligado && !limiteAtingido && (
        <div
          ref={composerRef}
          className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <Composer
            valor={texto}
            aoMudar={setTexto}
            aoEnviar={() => enviar(texto)}
            desabilitado={ocupado}
            mostrarBotaoChips={false}
            aoEscolherChip={() => {}}
            mostrarBotaoImagem
            aoEscolherImagem={() => inputRef.current?.click()}
            placeholder="Escreva aqui…"
          />
        </div>
      )}

      {!carregandoHistorico && !erroHistorico && ligado && limiteAtingido && (
        <div
          ref={composerRef}
          className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto w-full max-w-[720px] rounded-[18px] bg-background-50 p-4 shadow-pcard">
            <p className="text-[13px] font-medium text-warning-700">
              O limite de mensagens de hoje para o assistente de criação acabou.
            </p>
            <button
              type="button"
              onClick={() => navigate('/criar/formulario')}
              className="aperta mt-3 flex h-11 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-[14px] bg-primary-500 text-[14px] font-bold text-white transition-colors hover:bg-primary-600"
            >
              Preencher o formulário
            </button>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={(e) => aoEscolherArquivo(e.target.files?.[0] ?? null)}
      />

      {/* Menu ⋯ */}
      <Sheet aberto={sheetMenu} titulo="Opções" aoFechar={() => setSheetMenu(false)}>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              setSheetMenu(false);
              navigate('/criar/formulario');
            }}
            className="aperta flex min-h-[52px] w-full cursor-pointer items-center gap-3 rounded-[14px] bg-background-100 px-4 text-left text-[15px] font-semibold text-foreground-800 transition-colors hover:bg-background-200/70"
          >
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-file-list-3-line text-[20px]" aria-hidden />
            </span>
            Preencher o formulário
          </button>
          <button
            type="button"
            onClick={() => {
              setSheetMenu(false);
              setModalNovo(true);
            }}
            className="aperta flex min-h-[52px] w-full cursor-pointer items-center gap-3 rounded-[14px] bg-background-100 px-4 text-left text-[15px] font-semibold text-foreground-800 transition-colors hover:bg-background-200/70"
          >
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-add-line text-[20px]" aria-hidden />
            </span>
            Começar de novo
          </button>
          {uso && (
            <p className="px-1 pt-1 text-[12px] text-foreground-500">
              {uso.usadas} de {uso.limite} mensagens hoje.
            </p>
          )}
        </div>
      </Sheet>

      {/* Confirmar começar de novo */}
      <Modal
        aberto={modalNovo}
        titulo="Começar do zero?"
        aoFechar={() => setModalNovo(false)}
      >
        <p className="text-[15px] leading-relaxed text-foreground-600">
          Começar do zero? O rascunho que já foi criado continua em Meus eventos.
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => setModalNovo(false)}
            className="aperta h-12 w-full cursor-pointer whitespace-nowrap rounded-[16px] border border-background-300 bg-background-50 text-[15px] font-semibold text-foreground-800 sm:flex-1"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={comecarOutro}
            className="aperta h-12 w-full cursor-pointer whitespace-nowrap rounded-[16px] bg-primary-500 text-[15px] font-semibold text-white sm:flex-1"
          >
            Começar do zero
          </button>
        </div>
      </Modal>

      {/* Detalhes do rascunho (só leitura) */}
      <Sheet
        aberto={sheetDetalhes}
        titulo={estado?.evento.nome || 'Detalhes do evento'}
        aoFechar={() => setSheetDetalhes(false)}
      >
        {estado && (
          <div className="space-y-5">
            <div>
              <p className="text-[12px] font-bold uppercase tracking-wider text-foreground-400">
                Data e local
              </p>
              <p className="mt-1 text-[14px] text-foreground-800">
                {[linhaData(estado.evento.inicio_em), [estado.evento.cidade, estado.evento.uf].filter(Boolean).join('/')]
                  .filter(Boolean)
                  .join(' · ') || 'A definir'}
              </p>
            </div>

            <div>
              <p className="text-[12px] font-bold uppercase tracking-wider text-foreground-400">
                Participações
              </p>
              {estado.participacoes && estado.participacoes.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {estado.participacoes.map((p, i) => (
                    <div key={`${p.nome}-${i}`} className="rounded-[14px] bg-background-100 px-4 py-3">
                      <p className="text-[14px] font-semibold text-foreground-950">{p.nome}</p>
                      <p className="mt-0.5 text-[13px] text-foreground-600">
                        {[
                          textoValorParticipacao(p),
                          p.vagas != null ? `${p.vagas} vagas` : null,
                          p.grupos && p.grupos.length > 0 ? p.grupos.join(', ') : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-[13px] text-foreground-500">Ainda não definidas.</p>
              )}
            </div>

            <div>
              <p className="text-[12px] font-bold uppercase tracking-wider text-foreground-400">
                Encontros
              </p>
              {estado.encontros && estado.encontros.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {estado.encontros.map((e, i) => (
                    <div key={`${e.nome}-${i}`} className="flex items-center justify-between gap-3 rounded-[14px] bg-background-100 px-4 py-3">
                      <span className="min-w-0 truncate text-[14px] text-foreground-800">{e.nome}</span>
                      {e.data && (
                        <span className="shrink-0 text-[13px] text-foreground-500">{diaMes(e.data)}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-[13px] text-foreground-500">Ainda não definidos.</p>
              )}
            </div>

            {estado.abas_do_painel && estado.abas_do_painel.length > 0 && (
              <div>
                <p className="text-[12px] font-bold uppercase tracking-wider text-foreground-400">
                  Abas do painel
                </p>
                <p className="mt-1 text-[14px] text-foreground-800">
                  {estado.abas_do_painel.join(' · ')}
                </p>
              </div>
            )}
          </div>
        )}
      </Sheet>

      <VisualizadorArte
        aberto={!!imagemAberta}
        imagem={imagemAberta}
        alt="Imagem enviada"
        titulo="Arte"
        onFechar={() => setImagemAberta(null)}
      />
    </AppScreen>
  );
}